// Rankings (G-77, G-78): commanders and alliances by points (building levels of owned systems).
import type { AllianceRanking, PlayerRanking } from '@stars-and-ships/api-types';
import { useOutletContext, useSearchParams } from 'react-router';
import { useRankings } from '../api/queries';
import { Emblem } from '../components/alliance/Emblem';
import type { GameContext } from '../components/GameLayout';
import { AllianceTagLink, PlayerLink } from '../components/ProfileLinks';
import {
  EmptyState,
  ErrorNotice,
  Page,
  Panel,
  Spinner,
  type TabItem,
  TabPanel,
  Tabs,
} from '../components/ui';

type RankingTab = 'players' | 'alliances';
const TABS: TabItem<RankingTab>[] = [
  { id: 'players', label: 'Players' },
  { id: 'alliances', label: 'Alliances' },
];

const rowClass = (own: boolean) =>
  `grid grid-cols-[2.5rem_1fr_auto] items-center gap-2 hud-cut-sm border p-3 text-sm ${
    own ? 'border-accent bg-accent/5' : 'border-line bg-void/60'
  }`;

export function RankingsScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const [params, setParams] = useSearchParams();
  const tab: RankingTab = params.get('tab') === 'alliances' ? 'alliances' : 'players';
  const rankings = useRankings(galaxyId);
  const myId = me?.playerId;
  const myAllianceId = me?.alliance?.id;
  const ownPlayer = rankings.data?.players.find((p) => p.playerId === myId);
  const ownAlliance = rankings.data?.alliances.find((a) => a.id === myAllianceId);
  const own = tab === 'players' ? ownPlayer : ownAlliance;
  return (
    <Page width="narrow">
      <h1 className="font-display text-2xl font-bold uppercase tracking-[0.08em] text-text">
        Rankings
      </h1>
      <p className="text-sm text-muted">
        One point per building level in every system owned; an alliance has the sum of its members.
      </p>
      <Tabs
        label="Rankings"
        tabs={TABS}
        value={tab}
        onChange={(next) => setParams(next === 'players' ? {} : { tab: next })}
      />
      <TabPanel id={tab}>
        {rankings.error ? <ErrorNotice error={rankings.error} /> : null}
        {!rankings.data && !rankings.error ? <Spinner /> : null}
        {rankings.data ? (
          <div className="space-y-3">
            {own ? (
              <p className="font-mono text-sm text-text" data-own-ranking>
                {tab === 'players' ? 'You' : 'Your alliance'}: #{own.rank} · {own.points} points
              </p>
            ) : null}
            <Panel>
              {tab === 'players' ? (
                <PlayerRows galaxyId={galaxyId} rows={rankings.data.players} myId={myId} />
              ) : (
                <AllianceRows
                  galaxyId={galaxyId}
                  rows={rankings.data.alliances}
                  myAllianceId={myAllianceId}
                />
              )}
            </Panel>
          </div>
        ) : null}
      </TabPanel>
    </Page>
  );
}

function PlayerRows({
  galaxyId,
  rows,
  myId,
}: {
  galaxyId: string;
  rows: PlayerRanking[];
  myId: string | undefined;
}) {
  return (
    <ol className="space-y-2">
      {rows.map((p) => (
        <li
          key={p.playerId}
          data-ranking-player={p.displayName}
          className={rowClass(p.playerId === myId)}
        >
          <span className="font-mono text-muted">#{p.rank}</span>
          <span className="min-w-0 truncate">
            <PlayerLink
              galaxyId={galaxyId}
              playerId={p.playerId}
              name={p.displayName}
              className="text-text"
            />
            {p.alliance ? (
              <>
                {' '}
                <AllianceTagLink
                  galaxyId={galaxyId}
                  allianceId={p.alliance.id}
                  tag={p.alliance.tag}
                />
              </>
            ) : null}
            <span className="block text-xs text-faint">
              {p.systems} system{p.systems === 1 ? '' : 's'}
            </span>
          </span>
          <span className="font-mono tabular-nums text-text" data-points>
            {p.points}
          </span>
        </li>
      ))}
    </ol>
  );
}

function AllianceRows({
  galaxyId,
  rows,
  myAllianceId,
}: {
  galaxyId: string;
  rows: AllianceRanking[];
  myAllianceId: string | undefined;
}) {
  if (rows.length === 0) return <EmptyState>No alliances yet.</EmptyState>;
  return (
    <ol className="space-y-2">
      {rows.map((a) => (
        <li key={a.id} data-ranking-alliance={a.tag} className={rowClass(a.id === myAllianceId)}>
          <span className="font-mono text-muted">#{a.rank}</span>
          <span className="flex min-w-0 items-center gap-2">
            <Emblem emblem={a.emblem} tag={a.tag} size="sm" />
            <span className="min-w-0 truncate">
              <span className="text-text">{a.name}</span>{' '}
              <AllianceTagLink galaxyId={galaxyId} allianceId={a.id} tag={a.tag} />
              <span className="block text-xs text-faint">
                {a.members} member{a.members === 1 ? '' : 's'} · {a.systems} system
                {a.systems === 1 ? '' : 's'}
              </span>
            </span>
          </span>
          <span className="font-mono tabular-nums text-text" data-points>
            {a.points}
          </span>
        </li>
      ))}
    </ol>
  );
}
